import { menuRoutes } from './routes';
import { getAuth, getRoles } from '@app/utils/auth.utils';
import { SC_MODULES } from './constants/sc.modules';

class RouteAuthentication {
    constructor() {
        this.MenuRoutes = menuRoutes;
    }

    /**
     * Nối prefix path của module cha vào path/link của item con.
     * Tránh dấu '/' trùng lặp.
     * @param {string} prefix - Prefix path từ parent (isModule = true)
     * @param {string} value - Path hoặc link gốc của item
     * @returns {string} - Path đã được nối
     */
    joinPath(prefix, value) {
        if (!prefix || !value) return value || prefix || '';
        // Bỏ trailing slash prefix, bỏ leading slash value nếu cần
        const base = prefix.endsWith('/') ? prefix.slice(0, -1) : prefix;
        const segment = value.startsWith('/') ? value : `/${value}`;
        return `${base}${segment}`;
    }

    /**
     * Gắn roles và nối prefix path vào route item.
     * @param {Object} item - Route item
     * @param {string} prefix - Prefix path từ parent module
     * @returns {Object} - Route item đã được enrich
     */
    enrichRoute(item, prefix = '') {
        return {
            ...item,
            ...(item.path ? { path: this.joinPath(prefix, item.path) } : {}),
            ...(item.link ? { link: this.joinPath(prefix, item.link) } : {}),
            roles: getRoles(SC_MODULES.SERVICE, item.functionId),
        };
    }

    /**
     * Thu thập đệ quy menu items đã được xác thực,
     * hỗ trợ children lồng nhau ở bất kỳ độ sâu nào.
     * Khi parent có isModule = true, prefix path sẽ được nối vào children.
     * @param {Array} items - Danh sách menu items cần duyệt
     * @param {string} prefix - Prefix path từ parent module
     * @returns {Array} - Danh sách menu items đã lọc kèm roles
     */
    collectMenus(items, prefix = '') {
        return items
            .filter((item) => item.isMenu)
            .map((item) => {
                // Xác định prefix cho children: nếu item là module → dùng path của nó
                const childPrefix = item.isModule
                    ? this.joinPath(prefix, item.path || item.link || '')
                    : prefix;

                // Nếu menu có children → đệ quy lọc children
                if (item.children?.length > 0) {
                    const authorizedChildren = item.children
                        .filter(
                            (child) =>
                                child.isMenu &&
                                getAuth(SC_MODULES.SERVICE, child.functionId),
                        )
                        .map((child) => {
                            // Nếu child cũng có children → đệ quy tiếp
                            if (child.children?.length > 0) {
                                return {
                                    ...this.enrichRoute(child, childPrefix),
                                    children: this.collectMenus(
                                        child.children,
                                        childPrefix,
                                    ),
                                };
                            }
                            return this.enrichRoute(child, childPrefix);
                        });

                    return { ...item, children: authorizedChildren };
                }

                // Menu không có children nhưng là route
                if (item.isRoute) {
                    return this.enrichRoute(item, prefix);
                }

                return item;
            });
    }

    /**
     * Thu thập đệ quy tất cả route từ cây routes,
     * hỗ trợ children lồng nhau ở bất kỳ độ sâu nào.
     * Khi parent có isModule = true, prefix path sẽ được nối vào children.
     * @param {Array} items - Danh sách route items cần duyệt
     * @param {string} prefix - Prefix path từ parent module
     * @returns {Array} - Danh sách routes đã được flatten kèm roles
     */
    collectRoutes(items, prefix = '') {
        return items.flatMap((item) => {
            // Xác định prefix cho children: nếu item là module → dùng path của nó
            const childPrefix = item.isModule
                ? this.joinPath(prefix, item.path || item.link || '')
                : prefix;

            const current = item.isRoute
                ? [this.enrichRoute(item, prefix)]
                : [];
            const nested =
                item.children?.length > 0
                    ? this.collectRoutes(item.children, childPrefix)
                    : [];
            return [...current, ...nested];
        });
    }

    getMenu() {
        return this.collectMenus(this.MenuRoutes);
    }

    getRoutes() {
        return this.collectRoutes(this.MenuRoutes);
    }

    /**
     * Lấy danh sách module (isModule = true) ở cấp gốc (cha lớn nhất).
     * @returns {Array} - Danh sách module items kèm roles
     */
    getModule() {
        return this.MenuRoutes.filter((item) => item.isModule).map((item) =>
            this.enrichRoute(item),
        );
    }

    /**
     * Lấy menu theo module path.
     * Tìm module có path trùng khớp, rồi trả về menu children của module đó.
     * @param {string} modulePath - Path của module (vd: '/so-sach-ban-tru')
     * @returns {Array} - Danh sách menu items của module
     */
    getMenuByModule(modulePath) {
        if (!modulePath) return [];

        const module = this.MenuRoutes.find(
            (item) => item.isModule && item.path === modulePath,
        );

        if (!module?.children?.length) return [];

        return this.collectMenus(module.children, modulePath);
    }
}

export default new RouteAuthentication();
